import { ArrowRight, Check, LayoutGrid, Plus, Wrench } from "lucide-react";
import type { CatalogOffering, Listing } from "../../services/catalogApi";
import { InPlanBadge, blueprintPaper, primaryButton } from "./ui";

// Browse everything: the catalog's ready-made tools and forward-deployed services, and each service's own page.
// (An application's own page is the same item page the curated list uses.)

// What every service page promises, in the same words the plan and the call use.
const SERVICE_STEPS = ["Our team sets it up for you", "We can look after it for you once it is running",
                       "We go over timing and pricing with you on a short call"];

/** Everything in the catalog, as applications and services. Each card opens its own page, with "Add to my plan". */
export function BrowseAll({ listings, offerings, onStart, onOpen, onOpenService, added = [], services = [] }: {
  listings: Listing[];
  /** Plain-language service cards from the library, in their own section beneath the applications. */
  offerings?: CatalogOffering[];
  onStart: () => void;
  onOpen: (listing: Listing) => void;
  onOpenService?: (offering: CatalogOffering) => void;
  /** Ids of the applications and services already in the visitor's plan. */
  added?: string[];
  services?: string[];
}) {
  const card = "group flex flex-col items-start gap-2 rounded-2xl border border-borderSubtle bg-surface p-6 text-left shadow-sm transition-all hover:-translate-y-1 hover:border-primary/40 hover:shadow-xl hover:shadow-primary/10";
  const details = (
    <span className="mt-auto inline-flex items-center gap-1.5 pt-1 font-bold text-primary">
      See details<ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" aria-hidden="true" />
    </span>
  );
  return (
    <div className="flex flex-col gap-8">
      <div className="catalog-enter flex flex-wrap items-center justify-between gap-4 rounded-2xl border border-primary/20 bg-primary/5 px-6 py-5">
        <p className="text-lg font-semibold text-text-primary">Want a list made for your business? It takes about 3 minutes.</p>
        <button type="button" onClick={onStart} className={primaryButton}>Get my curated list</button>
      </div>
      {!!offerings?.length && (
        <section aria-labelledby="services-browse-title" className="flex flex-col gap-4">
          <div>
            <h2 id="services-browse-title" className="flex items-center gap-2 font-display text-2xl font-bold text-text-primary">
              <Wrench className="h-5 w-5 text-primary" aria-hidden="true" /> Forward-deployed services
            </h2>
            <p className="text-text-secondary">
              The services our tools run on, set up by our team inside your own environment and looked after for you.
              Add one on its own, or alongside a tool.
            </p>
          </div>
          <div className="catalog-stagger grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {offerings.map((o) => (
              <button key={o.id} type="button" onClick={() => onOpenService?.(o)} className={card}>
                <span className="flex flex-wrap items-center gap-2">
                  <span className="font-display text-lg font-bold text-text-primary">{o.name ?? o.headline}</span>
                  {services.includes(o.id) && <InPlanBadge />}
                </span>
                <span className="text-text-secondary">{o.summary}</span>
                {details}
              </button>
            ))}
          </div>
        </section>
      )}
      <section aria-labelledby="applications-title" className="flex flex-col gap-4">
        <div>
          <h2 id="applications-title" className="flex items-center gap-2 font-display text-2xl font-bold text-text-primary">
            <LayoutGrid className="h-5 w-5 text-primary" aria-hidden="true" /> Ready-made tools
          </h2>
          <p className="text-text-secondary">
            Applications built for your team, ready for any environment: your cloud, your office, or the apps you
            already use. Each one takes a job off your team's plate.
          </p>
        </div>
        <div className="catalog-stagger grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {listings.map((l) => (
            <button key={l.id} type="button" onClick={() => onOpen(l)} className={card}>
              <span className="flex flex-wrap items-center gap-2">
                <span className="font-display text-xl font-bold text-text-primary">{l.name}</span>
                {added.includes(l.id) && <InPlanBadge />}
              </span>
              <span className="text-text-secondary">{l.headline}</span>
              <span className="pt-2 text-sm font-semibold text-primary">Solves: {l.problems.join(", ")}</span>
              <span className="text-sm font-semibold text-text-muted">Time saved: {l.time_saved}</span>
              {details}
            </button>
          ))}
        </div>
      </section>
    </div>
  );
}

/** One service's own page: what it is, how it works, the applications it is set up alongside, and the plan. */
export function ServicePage({ offering, added, alongside, onToggle, onOpenListing, onBack }: {
  offering: CatalogOffering;
  added: boolean;
  /** Applications that come with this service set up beside them. */
  alongside: Listing[];
  onToggle: () => void;
  onOpenListing: (listing: Listing) => void;
  onBack: () => void;
}) {
  // Laid out like an application's page: the title, then the steps on the blueprint beside what it is.
  return (
    <article aria-labelledby="service-title" className="flex flex-col gap-6">
      <div className="flex flex-col gap-1.5">
        <span className="inline-flex items-center gap-1.5 text-xs font-extrabold uppercase tracking-wider text-accentPurple">
          <Wrench className="h-3.5 w-3.5" aria-hidden="true" />Forward-deployed service
        </span>
        <div className="flex flex-wrap items-center gap-3">
          <h2 id="service-title" className="font-display text-3xl font-bold text-text-primary sm:text-4xl">{offering.name ?? offering.headline}</h2>
          {added && <InPlanBadge />}
        </div>
      </div>

      <div className="flex flex-wrap items-start gap-8">
        <div role="region" aria-labelledby="service-how-title" style={blueprintPaper}
             className="flex min-w-0 flex-[3_1_520px] flex-col gap-4 rounded-2xl border border-primary/20 p-6 sm:p-8">
          <h3 id="service-how-title" className="self-start rounded-full bg-primary px-3 py-1 text-xs font-extrabold uppercase tracking-wider text-white">
            How it works
          </h3>
          <ol className="catalog-stagger flex flex-col gap-3">
            {SERVICE_STEPS.map((line, i) => (
              <li key={line} className="flex items-center gap-3 rounded-xl border-[1.5px] border-primary/40 bg-white px-4 py-3.5 text-[15px] font-semibold text-text-primary shadow-md">
                <span aria-hidden="true" className="flex h-7 w-7 flex-none items-center justify-center rounded-full bg-primary text-sm font-extrabold text-white">
                  {i + 1}
                </span>
                {line}
              </li>
            ))}
          </ol>
        </div>

        <div className="flex min-w-0 flex-[2_1_340px] flex-col gap-3.5">
          {offering.name && <p className="font-display text-2xl font-bold leading-snug text-text-primary">{offering.headline}</p>}
          <p className="text-lg text-text-secondary">{offering.summary}</p>
          {offering.description && (
            <div role="region" aria-labelledby="service-what-title" className="flex flex-col gap-2 border-t border-borderSubtle pt-4">
              <h3 id="service-what-title" className="flex items-center gap-2 font-display text-lg font-bold text-text-primary">
                <Check className="h-4 w-4 text-primary" aria-hidden="true" />What you get
              </h3>
              <p className="text-text-secondary">{offering.description}</p>
            </div>
          )}
        </div>
      </div>
      {alongside.length > 0 && (
        <section aria-labelledby="service-alongside-title" className="flex flex-col gap-3">
          <h3 id="service-alongside-title" className="font-display text-xl font-bold text-text-primary">Set up alongside</h3>
          <div className="catalog-stagger grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {alongside.map((l) => (
              <button key={l.id} type="button" onClick={() => onOpenListing(l)}
                      className="flex flex-col items-start gap-1 rounded-xl border border-borderSubtle bg-surface p-5 text-left hover:border-primary/40">
                <span className="text-lg font-bold text-text-primary">{l.name}</span>
                <span className="text-text-secondary">{l.headline}</span>
              </button>
            ))}
          </div>
        </section>
      )}
      <div className="sticky bottom-0 z-20 pb-4 pt-3.5">
        <div className="flex flex-wrap items-center gap-2 rounded-2xl border border-borderSubtle bg-white p-2.5 shadow-xl">
          <button type="button" aria-pressed={added} onClick={onToggle}
                  className={`inline-flex min-h-[52px] flex-auto items-center justify-center gap-2 rounded-xl border-2 px-5 font-bold text-primary ${
                    added ? "border-primary bg-primary/10" : "border-primary/40 bg-white"}`}>
            {added ? <Check className="h-4 w-4" aria-hidden="true" /> : <Plus className="h-4 w-4" aria-hidden="true" />}
            {added ? "In my plan" : "Add to my plan"}
          </button>
          <button type="button" onClick={onBack}
                  className="-order-1 inline-flex min-h-[60px] flex-[1_1_100%] items-center justify-center gap-2.5 rounded-xl bg-primary px-6 text-lg font-extrabold text-white hover:bg-primaryHover sm:order-none sm:flex-[0_1_auto]">
            Keep browsing<ArrowRight className="h-5 w-5" aria-hidden="true" />
          </button>
        </div>
      </div>
    </article>
  );
}
